import api from "../api";

// State of a BOT connection's warmed browser profile. Source of truth is the
// S3 metadata sidecar, so this is exactly what the store knows.
export interface BrowserProfileStatus {
  connection: number;
  label: string;
  warmed: boolean;
  warmed_at: string | null;
  warmed_by: string;
  identity_fingerprint: string;
  chrome_version: string;
  archive_bytes: number;
}

export type WarmingSessionStatus =
  | "PROVISIONING"
  | "READY"
  | "SAVING"
  | "DONE"
  | "FAILED"
  | "CANCELLED";

export interface BrowserWarmingSession {
  id: number;
  connection: number;
  status: WarmingSessionStatus;
  requested_action: "NONE" | "SAVE" | "CANCEL";
  started_by: string;
  error_message: string;
  created_at: string;
  expires_at: string;
  finished_at: string | null;
  // True on LOCAL_DEV stands: a developer must run the host-side
  // `browser_warming_worker` for the session to progress. Deployed
  // environments serve sessions with the ECS warming task.
  requires_local_worker: boolean;
  // Deployed only: noVNC page URL with a fresh signed access token, present
  // while the session is interactive (READY). Re-minted on every poll.
  live_view_url: string | null;
}

export const WARMING_TERMINAL_STATUSES: WarmingSessionStatus[] = [
  "DONE",
  "FAILED",
  "CANCELLED",
];

export const browserProfileTag = (connectionId: number) => ({
  type: "BrowserProfile" as const,
  id: connectionId,
});

export const browserProfilesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getBrowserProfile: builder.query<BrowserProfileStatus, number>({
      query: (connectionId) => ({
        url: `/integration-platform/browser-profiles/connections/${connectionId}/`,
        method: "GET",
      }),
      providesTags: (_result, _err, connectionId) => [
        browserProfileTag(connectionId),
      ],
    }),

    startBrowserWarming: builder.mutation<BrowserWarmingSession, number>({
      query: (connectionId) => ({
        url: `/integration-platform/browser-profiles/connections/${connectionId}/warm/`,
        method: "POST",
      }),
    }),

    // Polled by the warming dialog while it is open; no cache tags on
    // purpose - the poll IS the freshness mechanism.
    getBrowserWarmingSession: builder.query<BrowserWarmingSession, number>({
      query: (sessionId) => ({
        url: `/integration-platform/browser-profiles/warming-sessions/${sessionId}/`,
        method: "GET",
      }),
    }),

    saveBrowserWarming: builder.mutation<BrowserWarmingSession, number>({
      query: (sessionId) => ({
        url: `/integration-platform/browser-profiles/warming-sessions/${sessionId}/save/`,
        method: "POST",
      }),
    }),

    cancelBrowserWarming: builder.mutation<BrowserWarmingSession, number>({
      query: (sessionId) => ({
        url: `/integration-platform/browser-profiles/warming-sessions/${sessionId}/cancel/`,
        method: "POST",
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetBrowserProfileQuery,
  useStartBrowserWarmingMutation,
  useGetBrowserWarmingSessionQuery,
  useSaveBrowserWarmingMutation,
  useCancelBrowserWarmingMutation,
} = browserProfilesApi;
